import React, { Component } from "react";
import PropTypes from "prop-types";
import { LocationService, NoteService, LocationGroupsService } from "@services";
import utils from "@utils";
import { LOCATION_APPROVAL_TYPES_MAP, GOOGLE_MAP_SETTINGS, GOOGLE_MAP_OPTIONS } from "@constants";
import GoogleMap from "google-map-react";
import { Loader, Image, ScrollDetect, MapMarker } from "../../components";

import "./LocationDetails.scss";

class LocationDetails extends Component {
	constructor(props) {
		super(props);
		this.state = {
			locationDetails: {},
			activeJobsList: [],
			jobHistoryList: [],
			officersList: [],
			notesList: [],
			locationDetailsLoading: false,
			notesLoading: false,
			activeJobsHasMore: false,
			jobHistoryHasMore: false,
			officersHasMore: false,
			activeJobsPageNumber: 1,
			jobHistoryPageNumber: 1,
			officersPageNumber: 1,
			activeJobsRequested: false,
			jobHistoryRequested: false,
			officersRequested: false,
			notesRequested: false,
			activeTab: "activeJobs",
		};
	}

	componentDidMount() {
		this.getLocationDetails();
		this.getActiveJobs();
	}

	/* ****** Service Methods ******* */

	/** Retrevies the details per each location its id */
	getLocationDetails = () => {
		const { lang } = global;
		const {
			router: {
				params: { id },
				routes,
			},
		} = this.props;
		const path = routes[1].path === "locationgroups" ? "getLocationGroupById" : "getById";
		const service =
			routes[1].path === "locationgroups" ? LocationGroupsService : LocationService;

		this.setState({ locationDetailsLoading: true });
		service[path](id, { includeStats: true })
			.then(({ data }) => {
				this.setState({ locationDetails: data, locationDetailsLoading: false });
			})
			.catch(error => {
				utils.notify({
					type: "error",
					message:
						error.response?.data?.Message ?? lang.t("Error loading location details"),
					title: "Error",
				});
			});
	};

	/** Retrevies a list of active jobs per location */
	getActiveJobs = () => {
		const { lang } = global;
		const {
			router: {
				params: { id },
				routes,
			},
		} = this.props;
		const { activeJobsPageNumber } = this.state;
		const path = routes[1].path === "locationgroups" ? "getJobsByLocationGroup" : "getJobs";
		const service =
			routes[1].path === "locationgroups" ? LocationGroupsService : LocationService;

		service[path](id, {
			unStaffed: true,
			status: 1,
			pageNumber: activeJobsPageNumber,
			displayCount: 25,
		})
			.then(({ data }) => {
				this.setState(prevState => ({
					activeJobsList: [...prevState.activeJobsList, ...data.ItemList],
					activeJobsHasMore: data.ItemList.length >= 25,
					activeJobsPageNumber: prevState.activeJobsPageNumber + 1,
					activeJobsRequested: true,
				}));
			})
			.catch(error => {
				this.setState({
					activeJobsHasMore: false,
					activeJobsPageNumber: 1,
					activeJobsRequested: false,
				});
				utils.notify({
					type: "error",
					message: error.response?.data?.Message ?? lang.t("Error loading jobs"),
					timeOut: 1500,
				});
			});
	};

	/** Retrevies a list of job history per location */
	getJobHistory = () => {
		const { lang } = global;
		const {
			router: {
				params: { id },
				routes,
			},
		} = this.props;
		const { jobHistoryPageNumber } = this.state;
		const path = routes[1].path === "locationgroups" ? "getJobsByLocationGroup" : "getJobs";
		const service =
			routes[1].path === "locationgroups" ? LocationGroupsService : LocationService;

		service[path](id, {
			unStaffed: true,
			status: 3,
			pageNumber: jobHistoryPageNumber,
			displayCount: 25,
		})
			.then(({ data }) => {
				this.setState(prevState => ({
					jobHistoryList: [...prevState.jobHistoryList, ...data.ItemList],
					jobHistoryHasMore: data.ItemList.length >= 25,
					jobHistoryPageNumber: prevState.jobHistoryPageNumber + 1,
					jobHistoryRequested: true,
				}));
			})
			.catch(error => {
				this.setState({
					jobHistoryHasMore: false,
					jobHistoryPageNumber: 1,
					jobHistoryRequested: false,
				});
				utils.notify({
					type: "error",
					message: error.response?.data?.Message ?? lang.t("Error loading job history"),
					timeOut: 1500,
				});
			});
	};

	/** Retrevies a list of officers ssigned to a job per location */
	getOfficers = () => {
		const { lang } = global;
		const {
			router: {
				params: { id },
				routes,
			},
		} = this.props;
		const { officersPageNumber } = this.state;
		const path =
			routes[1].path === "locationgroups" ? "getOfficersByLocationGroup" : "getOfficers";
		const service =
			routes[1].path === "locationgroups" ? LocationGroupsService : LocationService;
		service[path](id, {
			pageNumber: officersPageNumber,
			displayCount: 25,
		})
			.then(({ data }) => {
				this.setState(prevState => ({
					officersList: [...prevState.officersList, ...data.ItemList],
					officersHasMore: data.ItemList.length >= 25,
					officersPageNumber: prevState.officersPageNumber + 1,
					officersRequested: true,
				}));
			})
			.catch(error => {
				this.setState({
					officersHasMore: false,
					officersPageNumber: 1,
					officersRequested: false,
				});
				utils.notify({
					type: "error",
					message: error.response?.data?.Message ?? lang.t("Error loading officers"),
					timeOut: 1500,
				});
			});
	};

	/** Retrevies a list of notes uploaded to a job */
	getNotes = () => {
		const { lang } = global;
		const {
			router: {
				params: { id },
				routes,
			},
		} = this.props;
		const queryParams = {
			displayCount: 10,
			pagingToken: undefined,
		};
		if (routes[1].path === "locationgroups") {
			queryParams.locationGroupId = id;
		} else {
			queryParams.locationId = id;
		}

		this.setState({ notesLoading: true });
		NoteService.getAll(queryParams)
			.then(({ data }) => {
				this.setState(prevState => ({
					notesList: [...prevState.notesList, ...data.ItemList],
					notesLoading: false,
					notesRequested: true,
				}));
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error getting Location Notes"),
					title: "Error",
				});
			});
	};
	/* ****** Service Methods ******* */

	/**
	 * Selects the active tab as well as the correct list of details to display
	 * @param {String} activeTab The tab which will be active and list of details which will display
	 */
	setActiveTab = activeTab => {
		const { activeJobsRequested, jobHistoryRequested, officersRequested, notesRequested } =
			this.state;

		switch (activeTab) {
			case "jobHistory":
				this.setState(
					prevState => ({
						activeTab: "jobHistory",
						jobHistoryPageNumber: jobHistoryRequested
							? prevState.jobHistoryPageNumber
							: 1,
					}),
					() => {
						if (!jobHistoryRequested) {
							this.getJobHistory();
						}
					},
				);
				break;
			case "officers":
				this.setState(
					prevState => ({
						activeTab: "officers",
						officersPageNumber: officersRequested ? prevState.officersPageNumber : 1,
					}),
					() => {
						if (!officersRequested) {
							this.getOfficers();
						}
					},
				);
				break;
			case "notes":
				this.setState(
					{
						activeTab: "notes",
						notesLoading: !notesRequested,
					},
					() => {
						if (!notesRequested) {
							this.getNotes();
						}
					},
				);
				break;
			case "activeJobs":
				this.setState(prevState => ({
					activeTab: "activeJobs",
					activeJobsPageNumber: activeJobsRequested ? prevState.activeJobsPageNumber : 1,
				}));
				break;
			default:
				break;
		}
	};

	/**
	 * Checks for the approval status per location determined by comparing he locations ApprovedType to LOCATION_APPROVAL_TYPES_MAP
	 * @returns {HTMLElement} UI which will display the approval status per location
	 */
	renderApprovedStatus = () => {
		const { locationDetails } = this.state;
		const locationApprovedType = LOCATION_APPROVAL_TYPES_MAP.find(
			item => item.type === locationDetails?.ApprovedType,
		);
		let color = "text-success";

		if (locationApprovedType?.color === "danger") {
			color = "text-danger";
		} else if (locationApprovedType?.color === "pending") {
			color = "text-warning";
		}

		return (
			<div className={color}>
				<i className={`${locationApprovedType?.icon} status-icon`} />
				<span className="status-text">{locationDetails?.ApprovedTypeDescription}</span>
			</div>
		);
	};

	render() {
		const { lang } = global;
		const { router, user } = this.props;
		const {
			locationDetails,
			activeJobsList,
			jobHistoryList,
			officersList,
			notesList,
			locationDetailsLoading,
			notesLoading,
			activeTab,
			activeJobsHasMore,
			jobHistoryHasMore,
			officersHasMore,
			activeJobsRequested,
			jobHistoryRequested,
			officersRequested,
			notesRequested,
		} = this.state;

		const center = {
			lat: parseFloat(locationDetails.Latitude ?? GOOGLE_MAP_SETTINGS.defaultCenter.lat),
			lng: parseFloat(locationDetails.Longitude ?? GOOGLE_MAP_SETTINGS.defaultCenter.lng),
		};

		const formattedAddress = utils.concatenateAddressDetails(
			{
				address: [locationDetails.StreetAddress1, locationDetails.StreetAddress2].join(" "),
				city: locationDetails.City,
				state: locationDetails.State,
				zip: locationDetails.Zipcode,
			},
			{ useMultiline: true },
		);

		return (
			<div id="location-groups">
				<div className="container-fluid">
					<div className="row">
						<div className="text-left">
							<button
								type="button"
								className="btn btn-link"
								onClick={() => router.push("/admin/location/list")}
							>
								<i className="rkf rkf-chevron-left" />
								&nbsp;
								{lang.t("Location Management")}
							</button>
						</div>
					</div>
				</div>
				<div className="container">
					<div className="row">
						<div className="text-left space-bottom col-xs-12">
							<h1>{lang.t("Location Details")}</h1>
						</div>
					</div>
					<div className="row">
						{/* Map Panel */}
						<div className="col-md-4">
							<div className="map-panel">
								<div className="row map-display">
									<GoogleMap
										defaultZoom={15}
										center={center}
										defaultCenter={GOOGLE_MAP_SETTINGS.defaultCenter}
										bootstrapURLKeys={{ key: GOOGLE_MAP_SETTINGS.key }}
										options={{ ...GOOGLE_MAP_OPTIONS, mapTypeControl: false }}
									>
										<MapMarker
											lat={center.lat}
											lng={center.lng}
										/>
									</GoogleMap>
								</div>
								<div className="row">
									<div className="text-center">
										<h4>{locationDetails.Name}</h4>
										<div className="text-muted">{formattedAddress.top}</div>
										<div className="text-muted">{formattedAddress.bottom}</div>
										{this.renderApprovedStatus()}
									</div>
								</div>

								<div className="separator separator-solid" />

								<div className="row">
									<div className="col-xs-1">
										<i className="rkf rkf-dollar-circle text-muted" />
									</div>
									<div className="col-xs-10 text-left">
										<div className="text-muted">{lang.t("Avg Rate")}</div>
										<div>
											{`$${parseFloat(locationDetails.AverageOfficerRate || 0).toFixed(2)}/hr`}
										</div>
									</div>
								</div>

								<div className="separator separator-solid" />

								<div className="row">
									<div className="col-xs-1">
										<i className="rkf rkf-analytics text-muted" />
									</div>
									<div className="col-xs-10 text-muted text-left space-bottom">
										{lang.t("Key Stats")}
									</div>
									<div className="col-xs-12">
										<div className="row">
											<div className="col-xs-4 text-center">
												<div
													className="text-muted"
													title={lang.t("Total Jobs")}
												>
													{lang.t("Jobs")}
												</div>
												<div>
													{utils.abbreviateNumber(
														locationDetails.TotalJobCount,
													) || 0}
												</div>
											</div>
											<div className="col-xs-4 text-center">
												<div
													className="text-muted"
													title={lang.t("Total Jobs")}
												>
													{lang.t("Hours")}
												</div>
												{utils.abbreviateNumber(
													parseFloat(
														locationDetails.TotalJobHours || 0,
													).toFixed(2),
												)}
											</div>
											<div className="col-xs-4 text-center">
												<div
													className="text-muted"
													title={lang.t("Unique Officers")}
												>
													{lang.t("Officers")}
												</div>
												<div>
													{utils.abbreviateNumber(
														locationDetails.UniqueOfficersCount,
													) || 0}
												</div>
											</div>
										</div>
									</div>
								</div>
								<Loader
									active={locationDetailsLoading}
									size="small"
								/>
							</div>
						</div>
						{/* Map Panel */}

						{/* Job Details Panel */}
						<div className="col-md-8">
							<div className="job-details-panel">
								<div className="row tab-list">
									<div
										className={`col-xs-3 text-center tab-button${activeTab === "activeJobs" ? " active" : ""}`}
									>
										<button
											type="button"
											id="active-jobs-button"
											className="btn btn-link"
											onClick={() => this.setActiveTab("activeJobs")}
										>
											{lang.t("Active Jobs")}
										</button>
									</div>
									<div
										className={`col-xs-3 text-center tab-button${activeTab === "jobHistory" ? " active" : ""}`}
									>
										<button
											type="button"
											id="job-history-button"
											className="btn btn-link"
											onClick={() => this.setActiveTab("jobHistory")}
										>
											{lang.t("Job History")}
										</button>
									</div>
									<div
										className={`col-xs-3 text-center tab-button${activeTab === "officers" ? " active" : ""}`}
									>
										<button
											type="button"
											id="officers-button"
											className="btn btn-link"
											onClick={() => this.setActiveTab("officers")}
										>
											{lang.t("Officers")}
										</button>
									</div>
									<div
										className={`col-xs-3 text-center tab-button${activeTab === "notes" ? " active" : ""}`}
									>
										<button
											type="button"
											id="notes-button"
											className="btn btn-link"
											onClick={() => this.setActiveTab("notes")}
										>
											{lang.t("Notes")}
										</button>
									</div>
								</div>
								<div className="row details-container">
									<div className="col-xs-12">
										<Choose>
											<When condition={activeTab === "activeJobs"}>
												<Choose>
													<When
														condition={
															activeJobsRequested &&
															activeJobsList.length === 0 &&
															!activeJobsHasMore
														}
													>
														<div className="row">
															<div className="text-center text-muted display-empty">
																{lang.t("No active jobs")}
															</div>
														</div>
													</When>
													<When condition={!activeJobsRequested}>
														<div className="text-center">
															<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
														</div>
													</When>
													<Otherwise>
														<For
															each="activeJob"
															of={activeJobsList}
															index="index"
														>
															<div
																className="row row-item v-align-sm"
																key={`activeJob_${activeJob.Id}`}
															>
																<div className="col-sm-4">
																	{utils.formatDate(
																		new Date(
																			`${activeJob.StartDateTime}Z`,
																		),
																		"en-US",
																		user.TimeZoneId,
																		"2-digit",
																	)}
																</div>
																<div className="col-sm-5">
																	<Choose>
																		<When
																			condition={
																				activeJob
																					.AssignedOfficers
																					.length > 0
																			}
																		>
																			<For
																				each="officer"
																				of={
																					activeJob
																						.AssignedOfficers
																						.length > 3
																						? activeJob.AssignedOfficers.slice(
																								0,
																								3,
																							)
																						: activeJob.AssignedOfficers
																				}
																				index="index"
																			>
																				<If
																					condition={
																						activeJob
																							.AssignedOfficers
																							.length ===
																						1
																					}
																				>
																					<Image
																						key={`activeJob_${officer.Id}`}
																						className="profile-image"
																						src={
																							officer.ProfilePhotoUrl ||
																							""
																						}
																						fallbackSrc="profile"
																						alt="profile"
																						title={
																							officer.FullName
																						}
																					/>
																					<div className="text-muted image-info">
																						<div>
																							{
																								officer.FullName
																							}
																						</div>
																						<div>
																							{
																								officer.OfficerRank
																							}
																						</div>
																					</div>
																				</If>
																				<If
																					condition={
																						activeJob
																							.AssignedOfficers
																							.length >=
																						2
																					}
																				>
																					<Image
																						key={`activeJob__${officer.Id}`}
																						className="profile-image"
																						src={
																							officer.ProfilePhotoUrl ||
																							""
																						}
																						fallbackSrc="profile"
																						alt="profile"
																						title={
																							officer.FullName
																						}
																					/>
																				</If>
																			</For>
																			<If
																				condition={
																					activeJob
																						.AssignedOfficers
																						.length > 3
																				}
																			>
																				<span className="text-muted">
																					{`+ ${
																						activeJob
																							.AssignedOfficers
																							.length -
																						3
																					}`}
																				</span>
																			</If>
																		</When>
																		<Otherwise>
																			<div className="text-muted">
																				{lang.t(
																					"Not Staffed",
																				)}
																			</div>
																		</Otherwise>
																	</Choose>
																</div>
																<div className="col-sm-3">
																	<button
																		type="button"
																		className="btn btn-link btn-block"
																		onClick={() =>
																			router.push(
																				`/jobs/${activeJob.Id}`,
																			)
																		}
																	>
																		{lang.t("View Details")}
																	</button>
																</div>
															</div>
														</For>
														<If
															condition={
																activeJobsHasMore &&
																activeJobsRequested
															}
														>
															<ScrollDetect
																className="text-center"
																onVisible={() =>
																	this.getActiveJobs()
																}
															>
																<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
															</ScrollDetect>
														</If>
													</Otherwise>
												</Choose>
											</When>
											<When condition={activeTab === "jobHistory"}>
												<Choose>
													<When
														condition={
															jobHistoryRequested &&
															jobHistoryList.length === 0 &&
															!jobHistoryHasMore
														}
													>
														<div className="row">
															<div className="text-center text-muted display-empty">
																{lang.t("No jobs worked")}
															</div>
														</div>
													</When>
													<When condition={!jobHistoryRequested}>
														<div className="text-center">
															<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
														</div>
													</When>
													<Otherwise>
														<For
															each="jobHistory"
															of={jobHistoryList}
															index="index"
														>
															<div
																className="row row-item v-align-sm"
																key={`jobHistory_${jobHistory.Id}`}
															>
																<div className="col-sm-4">
																	{utils.formatDate(
																		new Date(
																			`${jobHistory.StartDateTime}Z`,
																		),
																		"en-US",
																		user.TimeZoneId,
																		"2-digit",
																	)}
																</div>
																<div className="col-sm-5">
																	<Choose>
																		<When
																			condition={
																				jobHistory
																					.AssignedOfficers
																					.length > 0
																			}
																		>
																			<For
																				each="officer"
																				of={
																					jobHistory
																						.AssignedOfficers
																						.length > 3
																						? jobHistory.AssignedOfficers.slice(
																								0,
																								3,
																							)
																						: jobHistory.AssignedOfficers
																				}
																				index="index"
																			>
																				<If
																					condition={
																						jobHistory
																							.AssignedOfficers
																							.length ===
																						1
																					}
																				>
																					<Image
																						key={`activeJob_${officer.Id}`}
																						className="profile-image"
																						src={
																							officer.ProfilePhotoUrl ||
																							""
																						}
																						fallbackSrc="profile"
																						alt="profile"
																						title={
																							officer.FullName
																						}
																					/>
																					<div className="text-muted image-info">
																						<div>
																							{
																								officer.FullName
																							}
																						</div>
																						<div>
																							{
																								officer.OfficerRank
																							}
																						</div>
																					</div>
																				</If>
																				<If
																					condition={
																						jobHistory
																							.AssignedOfficers
																							.length >=
																						2
																					}
																				>
																					<Image
																						key={`activeJob__${officer.Id}`}
																						className="profile-image"
																						src={
																							officer.ProfilePhotoUrl ||
																							""
																						}
																						fallbackSrc="profile"
																						alt="profile"
																						title={
																							officer.FullName
																						}
																					/>
																				</If>
																			</For>
																			<If
																				condition={
																					jobHistory
																						.AssignedOfficers
																						.length > 3
																				}
																			>
																				<span className="text-muted">
																					{`+ ${
																						jobHistory
																							.AssignedOfficers
																							.length -
																						3
																					}`}
																				</span>
																			</If>
																		</When>
																		<Otherwise>
																			<div className="text-muted">
																				{lang.t(
																					"Not Staffed",
																				)}
																			</div>
																		</Otherwise>
																	</Choose>
																</div>
																<div className="col-sm-3">
																	<button
																		type="button"
																		className="btn btn-link btn-block"
																		onClick={() =>
																			router.push(
																				`/jobs/${jobHistory.Id}`,
																			)
																		}
																	>
																		{lang.t("View Details")}
																	</button>
																</div>
															</div>
														</For>
														<If
															condition={
																jobHistoryHasMore &&
																jobHistoryRequested
															}
														>
															<ScrollDetect
																className="text-center"
																onVisible={() =>
																	this.getJobHistory()
																}
															>
																<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
															</ScrollDetect>
														</If>
													</Otherwise>
												</Choose>
											</When>
											<When condition={activeTab === "officers"}>
												<Choose>
													<When
														condition={
															officersRequested &&
															officersList.length === 0 &&
															!officersHasMore
														}
													>
														<div className="row">
															<div className="text-center text-muted display-empty">
																{lang.t(
																	"No officers have worked at this location",
																)}
															</div>
														</div>
													</When>
													<When condition={!officersRequested}>
														<div className="text-center">
															<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
														</div>
													</When>
													<Otherwise>
														<For
															each="officer"
															of={officersList}
															index="index"
														>
															<div
																className="row row-item v-align-sm"
																key={`officer_${officer.Id}`}
															>
																<div className="col-sm-5">
																	<Image
																		className="profile-image"
																		src={
																			officer.ProfilePhotoUrl ||
																			""
																		}
																		fallbackSrc="profile"
																		alt="profile"
																		title={officer.FullName}
																	/>
																	<div className="text-muted image-info">
																		<div>
																			{officer.FullName}
																		</div>
																		<div>
																			{officer.OfficerRank ||
																				lang.t("Officer")}
																		</div>
																	</div>
																</div>
																<div className="col-xs-6 col-sm-3 text-muted text-center">
																	{`${lang.t("Jobs Worked")}: ${officer.TotalJobCount || 0}`}
																</div>
																<div className="col-xs-6 col-sm-4 text-muted text-center">
																	{`${lang.t("Hours Worked")}: ${parseFloat(
																		officer.TotalHoursWorked ||
																			0,
																	).toFixed(2)}`}
																</div>
															</div>
														</For>
														<If
															condition={
																officersHasMore && officersRequested
															}
														>
															<ScrollDetect
																className="text-center"
																onVisible={() => this.getOfficers()}
															>
																<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
															</ScrollDetect>
														</If>
													</Otherwise>
												</Choose>
											</When>
											<When condition={activeTab === "notes"}>
												<Choose>
													<When
														condition={
															notesRequested &&
															notesList.length === 0 &&
															!notesLoading
														}
													>
														<div className="row">
															<div className="text-center text-muted display-empty">
																{lang.t("No notes found")}
															</div>
														</div>
													</When>
													<When condition={!notesRequested}>
														<div className="text-center">
															<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
														</div>
													</When>
													<Otherwise>
														<For
															each="note"
															of={notesList}
															index="index"
														>
															<div
																className="row row-item v-align-sm"
																key={`note_${note.Id}`}
															>
																<div className="col-xs-4 col-sm-3">
																	{utils.formatDate(
																		new Date(
																			`${note.CreateDateUTC}Z`,
																		),
																		"en-US",
																		user.TimeZoneId,
																		"2-digit",
																	)}
																	<div className="text-muted">
																		{utils.formatTime(
																			new Date(
																				`${note.CreateDateUTC}Z`,
																			),
																			true,
																			user.TimeZoneId,
																		)}
																	</div>
																</div>
																<div className="col-xs-8 col-sm-9">
																	<If condition={note.Url}>
																		<Image
																			src={note.Url}
																			fallbackSrc="note-image"
																			alt="note image"
																			className="note-image"
																		/>
																	</If>
																	<div className="image-info">
																		{note.UserName}
																		&nbsp;
																		<div className="text-muted">
																			{note.Comment}
																		</div>
																	</div>
																</div>
															</div>
														</For>
														<If
															condition={
																notesLoading && notesRequested
															}
														>
															<ScrollDetect
																className="text-center"
																onVisible={() => this.getNotes()}
															>
																<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
															</ScrollDetect>
														</If>
													</Otherwise>
												</Choose>
											</When>
										</Choose>
									</div>
								</div>
							</div>
						</div>
						{/* Job Details Panel */}
					</div>
				</div>
			</div>
		);
	}
}

LocationDetails.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object,
};

LocationDetails.defaultProps = {
	user: {},
	router: {},
};

export default LocationDetails;
